import type { WorldSnapshot } from "@radar-sim/schema";

/**
 * Bounded ring buffer of recent snapshots. Used by the classroom client to
 * support local scrub-pause without disconnecting from the canonical world.
 *
 * Capacity is sized in seconds × tick rate. At 10 Hz, 5 minutes is 3000
 * snapshots — small enough to keep in memory on a tablet, plenty for a
 * student to flick back through the encounter that just happened.
 */
export class SnapshotBuffer {
  private readonly capacity: number;
  private buffer: WorldSnapshot[] = [];

  constructor(opts: { capacitySnapshots?: number; capacitySeconds?: number; tickRateHz?: number } = {}) {
    if (opts.capacitySnapshots !== undefined) {
      this.capacity = opts.capacitySnapshots;
    } else {
      const seconds = opts.capacitySeconds ?? 300;
      const hz = opts.tickRateHz ?? 10;
      this.capacity = Math.max(1, Math.round(seconds * hz));
    }
  }

  push(snapshot: WorldSnapshot): void {
    this.buffer.push(snapshot);
    if (this.buffer.length > this.capacity) {
      this.buffer.splice(0, this.buffer.length - this.capacity);
    }
  }

  get length(): number {
    return this.buffer.length;
  }

  get capacityLimit(): number {
    return this.capacity;
  }

  /** Most recent snapshot, or undefined if the buffer is empty. */
  latest(): WorldSnapshot | undefined {
    return this.buffer[this.buffer.length - 1];
  }

  /** Snapshot at index `i` from oldest (0). Returns undefined if out of range. */
  at(i: number): WorldSnapshot | undefined {
    if (i < 0 || i >= this.buffer.length) return undefined;
    return this.buffer[i];
  }

  /** Snapshot whose simTimeSeconds is closest to `t`. Returns undefined if empty. */
  nearest(t: number): WorldSnapshot | undefined {
    if (this.buffer.length === 0) return undefined;
    let best = this.buffer[0]!;
    let bestDiff = Math.abs(best.simTimeSeconds - t);
    for (const s of this.buffer) {
      const d = Math.abs(s.simTimeSeconds - t);
      if (d < bestDiff) {
        best = s;
        bestDiff = d;
      }
    }
    return best;
  }

  toArray(): ReadonlyArray<WorldSnapshot> {
    return [...this.buffer];
  }

  clear(): void {
    this.buffer = [];
  }
}
